import { useState, useCallback } from "react";
import { adminApi } from "../../api";

/**
 * State transition modal logic. Caller passes `onCommitted` to refresh the list.
 */
export default function useTransitionModal({ onCommitted } = {}) {
  const [order, setOrder] = useState(null);
  const [notes, setNotes] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [rastreamento, setRastreamento] = useState("");
  const [sending, setSending] = useState(false);

  const open = useCallback((o) => {
    setOrder(o); setNotes(""); setSendEmail(false); setRastreamento(o?.rastreamento || ""); setSending(false);
  }, []);
  const close = useCallback(() => {
    setOrder(null); setNotes(""); setSendEmail(false); setRastreamento(""); setSending(false);
  }, []);

  const transition = useCallback(async (newState) => {
    if (!order || sending) return;
    setSending(true);
    try {
      const r = await adminApi.orderTransition({
        order_id: order.ID_Encomenda,
        new_state: newState,
        observacao: notes,
        send_email: sendEmail ? "S" : "N",
        rastreamento: newState === "EV" ? rastreamento.trim() : undefined,
      });
      // Surface document-emission failures (NC/FT/RB) so the admin
      // doesn't silently end up with an inconsistent order (e.g. CC without
      // Nota de Crédito + commissions un-reversed). The state transition
      // itself succeeded, so we keep going — but warn loudly.
      const invoice = r?.invoice;
      if (invoice && invoice.success === false) {
        const msg = invoice.message || "Erro desconhecido";
        // eslint-disable-next-line no-alert
        alert(
          `Estado actualizado, mas a emissão do documento Moloni falhou:\n\n` +
          `${msg}\n\n` +
          `A encomenda foi marcada manualmente no novo estado, mas terá de gerar o documento (NC/FT) à mão no separador "Faturação".`
        );
      }
      close();
      onCommitted?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.message || "Erro ao transitar");
      setSending(false);
    }
  }, [order, sending, notes, sendEmail, rastreamento, close, onCommitted]);

  /**
   * Send a free-text message to the client tied to the current order,
   * without changing the order state. The message is stored in
   * `Trel_EstadosEncomenda` keeping `ID_EstadoEncomenda` unchanged so the
   * communication shows up in the order history.
   *
   * **Importante**: este botão envia SEMPRE email ao cliente — o seu nome
   * ("Enviar mensagem") implica notificação. A checkbox `sendEmail` aplica-se
   * apenas às transições de estado, NÃO a esta acção. Comportamento corrigido
   * em 2026-06 após reporte de admin que clicava no botão sem marcar a checkbox
   * e ficava sem notificar o cliente nem flag 'S' no histórico.
   */
  const sendMessage = useCallback(async () => {
    if (!order || sending) return;
    const message = notes.trim();
    if (!message) {
      // eslint-disable-next-line no-alert
      alert("Escreve uma mensagem antes de enviar.");
      return;
    }
    setSending(true);
    try {
      const r = await adminApi.orderMessage({
        order_id: order.ID_Encomenda,
        message,
        send_email: true,
      });
      if (r?.email_error) {
        // eslint-disable-next-line no-alert
        alert(`Mensagem registada no histórico, mas o envio do email falhou: ${r.email_error}`);
      }
      close();
      onCommitted?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.message || "Erro ao enviar mensagem");
      setSending(false);
    }
  }, [order, sending, notes, close, onCommitted]);

  return {
    order, notes, setNotes, sendEmail, setSendEmail,
    rastreamento, setRastreamento, sending,
    open, close, transition, sendMessage,
  };
}
